import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  ICall,
  IJointCall,
  ILinkedEntityRecord,
} from '../components/notetaker-card/notetaker-result/ICallInterface';
import { IQuestion } from '../types/IQuestionManager';

@Injectable({
  providedIn: 'root',
})
export class NotetakerCallsService {
  constructor(private http: HttpClient) {}

  public getCallsByEntity(corpId: string, entity: string, entityId: string): Observable<ICall[]> {
    return this.http
      .get<any>(
        `${environment.ntApiGatewayUrl}/calls/${corpId}/list?entity=${entity}&entityId=${entityId}`,
      )
      .pipe(
        map((result: ICall[]) => {
          result.forEach((call: ICall) => {
            call.date = new Date(call.date).toISOString();
          });
          return result;
        }),
      );
  }

  private getJointCallId(call: ICall): string {
    return call.callId.split('-')[3];
  }

  private buildJointCalls(calls: ICall[]): Record<string, IJointCall> {
    return calls.reduce(
      (a, call) => {
        const jointCallId = this.getJointCallId(call);
        if (!a[jointCallId]) {
          a[jointCallId] = {
            jointCallId: jointCallId,
            callTitle: call.description,
            caller: call.callerId,
            date: call.date,
            emails: [],
            entityIds: [],
          };
        }

        a[jointCallId].emails.push(call.attendeeEmail);
        a[jointCallId].entityIds.push(call.entityId);

        return a;
      },
      {} as Record<string, IJointCall>,
    );
  }

  // todo config bit
  public getCallsByCorpAndDate(
    corpId: string,
    startDate: Date,
    endData: Date,
  ): Observable<IJointCall[]> {
    const resultsPerRequest = 500;
    const url = `${environment.ntApiGatewayUrl}/manager/${corpId}?startDate=${startDate.toISOString()}&endDate=${endData.toISOString()}&limit=${resultsPerRequest}`;
    return this.getCallsPaginated(url).pipe(
      map((result: ICall[]) => {
        result.forEach((call: ICall) => {
          call.date = new Date(call.date).toISOString();
        });
        return Object.values(this.buildJointCalls(result));
      }),
    );
  }

  private getCallsPaginated(url: string, continuationToken?: string): Observable<ICall[]> {
    const params = continuationToken ? { continuationToken } : {};
    return this.http.get<any>(url, { params }).pipe(
      switchMap((response: { hasMore: boolean; continuationToken?: string; items: ICall[] }) => {
        const calls = response.items || [];
        if (response.hasMore && response.continuationToken) {
          return this.getCallsPaginated(url, response.continuationToken).pipe(
            map((nextPageCalls) => calls.concat(nextPageCalls)),
          );
        }
        return of(calls);
      }),
      catchError((err) => {
        console.error('Error fetching calls', err);
        return of([]);
      }),
    );
  }

  public getQuestionsByCorp(corpId: string): Observable<any[]> {
    return this.http.get<any>(`${environment.ntApiGatewayUrl}/questions/${corpId}`).pipe(
      map((result: any[]) => {
        return result.map((item) => {
          item.meta = this.parseMeta(item.meta);
          return item;
        });
      }),
    );
  }

  public saveQuestion(question: any): Observable<void> {
    return this.http.put<any>(
      `${environment.ntApiGatewayUrl}/questions/${question.corpId}/${question.sortKey}`,
      question,
    );
  }

  saveQuestionPackToConfig(payload: any): Observable<any> {
    const url = `${environment.ntApiGatewayUrl}/config?corpId=${payload.corpId}`;
    return this.http.put(url, payload);
  }

  updateCallOptions(corpId: string, callOptions: { saveTranscriptAsFile: boolean; oneNotePerCall?: boolean }): Observable<any> {
    const url = `${environment.ntApiGatewayUrl}/config?corpId=${corpId}`;
    return this.http.patch(url, callOptions);
  }

  deleteQuestion(question: IQuestion): Observable<any> {
    const url = `${environment.ntApiGatewayUrl}/questions/${question.corpId}/${question.sortKey}`;
    return this.http.delete(url);
  }

  // create meta object - pull neta from response - mapped to this object
  private parseMeta(metaStr: string): any {
    if (!metaStr || typeof metaStr !== 'string' || metaStr.trim() === '') {
      return {};
    }

    if (metaStr.startsWith('{') && metaStr.endsWith('}')) {
      metaStr = metaStr.substring(1, metaStr.length - 1);
    }
    const pairs = metaStr.split(/,\s*(?=\w+=)/);
    const metaObj: any = {};

    pairs.forEach((pair) => {
      const index = pair.indexOf('=');
      if (index !== -1) {
        const key = pair.substring(0, index).trim();
        const value = pair.substring(index + 1).trim();
        metaObj[key] = value;
      }
    });

    return metaObj;
  }

  public saveCall(call: ICall): Observable<void> {
    return this.http.put<any>(
      `${environment.ntApiGatewayUrl}/calls/${call.corpId}/${call.callId}`,
      call,
    );
  }

  public getLinkedEntityRecord(
    corpId: string,
    callId: string,
  ): Observable<ILinkedEntityRecord | null> {
    const callIdPrefix = callId.split('-')[0];
    if (!callIdPrefix) {
      throw new Error('Unable to find call id prefix');
    }
    return this.http
      .get<ILinkedEntityRecord>(
        `${environment.ntApiGatewayUrl}/calls/linkedentity/${corpId}/call/${callIdPrefix}`,
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(null);
          }
          // Re-throw other errors
          throw error;
        }),
      );
  }

  public putLinkedEntityRecord(linkedEntityRecord: ILinkedEntityRecord): Observable<void> {
    return this.http.put<void>(
      `${environment.ntApiGatewayUrl}/calls/linkedentity/${linkedEntityRecord.corpId}/call/${linkedEntityRecord.sortKey}`,
      {
        corpId: linkedEntityRecord.corpId,
        sortKey: linkedEntityRecord.sortKey,
        LinkedEntities: JSON.stringify(linkedEntityRecord.LinkedEntities),
      },
    );
  }

  public deleteCall(call: ICall): Observable<void> {
    return this.http.delete<void>(
      `${environment.ntApiGatewayUrl}/calls/${call.corpId}/${call.callId}`,
    );
  }
}
